import type { ContactSubmissionPayload } from './types'
import { fieldKey, getFormService, type ContactFormField } from './contact-form-utils'

/** Native date inputs store ISO (`yyyy-mm-dd`); API expects `mm/dd/yyyy`. */
function isoDateToApiFormat(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return value
  return `${match[2]}/${match[3]}/${match[1]}`
}

/**
 * Maps form values to the form-service POST body.
 * Internal RHF keys (`field_${id}`) are remapped to CMS `fieldName` for the API.
 * Boolean fields are serialized as `'true'` / `''`.
 * Date fields are sent as `mm/dd/yyyy` (native inputs keep ISO in the UI state).
 * `service` comes from `NEXT_PUBLIC_FORM_SERVICE`.
 * `extraFields` are merged in as-is (e.g. Builder name from a details page).
 */
export function buildContactPayload(
  fields: ContactFormField[],
  values: Record<string, string | boolean | undefined>,
  recaptchaToken: string,
  extraFields?: Record<string, string>,
): ContactSubmissionPayload {
  const dynamicFields: Record<string, string> = {}

  for (const field of fields) {
    const value = values[fieldKey(field)]
    if (typeof value === 'boolean') {
      dynamicFields[field.fieldName] = value ? 'true' : ''
    } else if (field.fieldType === 'Date' && typeof value === 'string') {
      dynamicFields[field.fieldName] = isoDateToApiFormat(value)
    } else {
      dynamicFields[field.fieldName] = value ?? ''
    }
  }

  return {
    data: {
      ...dynamicFields,
      ...extraFields,
      service: getFormService(),
      recaptchaToken,
    },
  }
}
